/**
 * Environment Configuration
 *
 * This module provides type-safe access to environment variables
 * and environment-specific configuration.
 *
 * Environments:
 * - dev: Local development (localhost)
 * - qa: QA environment (qa.creators.influenster.com)
 * - prod: Production (creators.influenster.com)
 */

export type Environment = 'dev' | 'qa' | 'prod';

interface EnvironmentConfig {
  env: Environment;
  siteBasePath: string;
  apiBasePath: string;
  memberGatewayBasePath: string;
  cognito: {
    domain: string;
    userPoolId: string;
    clientId: string;
    region: string;
    redirectSignIn: string;
    redirectSignOut: string;
  };
  instagram: {
    clientId: string;
    redirectUri: string;
  };
  features: {
    allowTestHarness: boolean;
  };
}

// Get environment with fallback
const getEnv = (): Environment => {
  const env = import.meta.env.VITE_ENV as string;
  if (['dev', 'qa', 'prod'].includes(env)) {
    return env as Environment;
  }
  return 'dev';
};

// Build configuration from environment variables
export const ENV: EnvironmentConfig = {
  env: getEnv(),
  siteBasePath: import.meta.env.VITE_SITE_BASEPATH || 'http://localhost:3000',
  apiBasePath: import.meta.env.VITE_API_BASE_PATH || '',
  memberGatewayBasePath: import.meta.env.VITE_MEMBER_GATEWAY_BASEPATH || '',
  cognito: {
    domain: import.meta.env.VITE_COGNITO_DOMAIN || '',
    userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID || '',
    clientId: import.meta.env.VITE_COGNITO_CLIENT_ID || '',
    region: import.meta.env.VITE_COGNITO_REGION || 'us-east-1',
    redirectSignIn:
      import.meta.env.VITE_COGNITO_REDIRECT_SIGN_IN || 'http://localhost:3000/auth-success',
    redirectSignOut: import.meta.env.VITE_COGNITO_REDIRECT_SIGN_OUT || 'http://localhost:3000',
  },
  instagram: {
    clientId: import.meta.env.VITE_INSTAGRAM_CLIENT_ID || '',
    // Must be registered in the Meta app. Set VITE_INSTAGRAM_REDIRECT_URI in .env.
    redirectUri:
      import.meta.env.VITE_INSTAGRAM_REDIRECT_URI || `${window.location.origin}/oauth/callback`,
  },
  features: {
    allowTestHarness: import.meta.env.VITE_ALLOW_TEST_HARNESS === 'true',
  },
};

// Helper functions
export const isDev = (): boolean => ENV.env === 'dev';
export const isQA = (): boolean => ENV.env === 'qa';
export const isProd = (): boolean => ENV.env === 'prod';
export const isProduction = (): boolean => ENV.env === 'prod';

export default ENV;
